import { useState } from 'react';
import { CheckCircle2, ExternalLink, GitBranch, RefreshCw, Rocket, XCircle } from 'lucide-react';
import { useActiveTenants } from '@/shared/hooks/useTenantsSearch.ts';
import {
  useDeployDraftList,
  useApproveDeployDraft,
  useRejectDeployDraft,
} from '../hooks/useDeployDrafts.ts';
import { DeployDraftReviewModal } from './DeployDraftReviewModal.tsx';
import type { DeployDraftRow, DeployDraftStatus } from '@/shared/types/deploy.ts';

/* ── constantes e helpers ─────────────────────────────── */

const STATUS_TABS: { value: DeployDraftStatus; label: string }[] = [
  { value: 'PENDENTE', label: 'Pendentes' },
  { value: 'APROVADO', label: 'Aprovados' },
  { value: 'REJEITADO', label: 'Rejeitados' },
];

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function duracaoMinutos(inicio: string, fim: string) {
  return Math.round((new Date(fim).getTime() - new Date(inicio).getTime()) / 60_000);
}

/* ── página ───────────────────────────────────────────── */

export function DeployDraftListPage() {
  const [status, setStatus] = useState<DeployDraftStatus>('PENDENTE');
  const [revisando, setRevisando] = useState<DeployDraftRow | null>(null);

  const { data: activeTenants } = useActiveTenants();
  const { data, isLoading, refetch, isFetching } = useDeployDraftList({
    page: 1,
    limit: 50,
    status,
  });

  const approveMutation = useApproveDeployDraft(() => setRevisando(null));
  const rejectMutation = useRejectDeployDraft(() => setRevisando(null));

  const drafts = data?.data ?? [];

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Deploys</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Pipelines concluídos aguardando revisão. Nenhum deles afeta o SLA antes de ser
            aprovado.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {/* Abas de status */}
      <div className="mb-4 flex gap-1 border-b border-gray-200 dark:border-gray-800">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatus(tab.value)}
            className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              status === tab.value
                ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <RefreshCw size={24} className="animate-spin text-gray-400" />
        </div>
      ) : drafts.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
          <Rocket size={28} />
          <p className="text-sm">
            {status === 'PENDENTE'
              ? 'Nenhum deploy aguardando revisão'
              : 'Nenhum deploy neste status'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-medium">{draft.tituloSugerido}</h3>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      <GitBranch size={11} />
                      {draft.branch}
                    </span>
                  </div>

                  <p className="mt-0.5 font-mono text-xs text-gray-500">
                    {draft.workspace}/{draft.repoSlug}
                    {draft.autor ? ` · ${draft.autor}` : ''}
                  </p>

                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {fmtDate(draft.iniciadoEm)} → {fmtDate(draft.encerradoEm)}{' '}
                    <span className="text-gray-400">
                      ({duracaoMinutos(draft.iniciadoEm, draft.encerradoEm)} min)
                    </span>
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {draft.tenants.length === 0 ? (
                      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        sem tenants mapeados — escolha na revisão
                      </span>
                    ) : (
                      draft.tenants.map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        >
                          {t.nome}
                        </span>
                      ))
                    )}
                  </div>

                  {draft.status === 'APROVADO' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle2 size={13} />
                      Aprovado por {draft.revisadoPor}
                      {draft.revisadoEm ? ` em ${fmtDate(draft.revisadoEm)}` : ''}
                    </p>
                  )}

                  {draft.status === 'REJEITADO' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                      <XCircle size={13} />
                      Rejeitado por {draft.revisadoPor}: {draft.motivoRejeicao}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  {draft.status === 'PENDENTE' && (
                    <button
                      onClick={() => setRevisando(draft)}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      Revisar
                    </button>
                  )}
                  {draft.pipelineUrl && (
                    <a
                      href={draft.pipelineUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Pipeline <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {revisando && (
        <DeployDraftReviewModal
          draft={revisando}
          tenants={activeTenants ?? []}
          isSubmitting={approveMutation.isPending || rejectMutation.isPending}
          onClose={() => setRevisando(null)}
          onApprove={(input) => approveMutation.mutate({ id: revisando.id, input })}
          onReject={(motivo) => rejectMutation.mutate({ id: revisando.id, motivo })}
        />
      )}
    </div>
  );
}
